/**
 * InvoiceList - Lista facturilor (emise/primite)
 *
 * Layout responsive:
 *   • Mobile (sub sm): cards verticale — text deasupra, butoane dedesubt (ca la Devize)
 *   • Desktop (sm+): tabel clasic
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateRO } from "@/utils/dateFormat";
import {
  Eye, Edit, Download, Send, CheckCircle, Code,
  Trash2, Globe, RefreshCw, Loader2, Receipt,
} from "lucide-react";

const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "0,00";
  return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

/**
 * Bara de acțiuni — folosită identic în mobile-card și desktop-table.
 * Pe mobile, butoanele se așază pe rânduri (flex-wrap), cu icon + text dedesubt.
 */
function InvoiceActions({
  invoice, onView, onEdit, onDownloadPDF, onDownloadXML, onEmit,
  onSendToAnaf, onCheckAnafStatus, onPaid, onDelete,
  sendingToAnaf, checkingAnafStatus, anafConfigured, isMobile = false,
}) {
  const wrapperClass = isMobile
    ? "flex flex-wrap gap-1.5 justify-start"
    : "flex gap-1 justify-end flex-wrap";
  const btnBase = isMobile
    ? "flex-col h-auto py-1.5 px-2 gap-0 min-w-[58px]"
    : "flex-col sm:flex-row h-auto sm:h-9 py-1.5 sm:py-1 px-2 gap-0 sm:gap-1";

  const Label = ({ text }) => (
    isMobile ? (
      <span className="text-[10px] leading-tight mt-0.5">{text}</span>
    ) : (
      <>
        <span className="text-[9px] leading-tight mt-0.5 sm:hidden">{text}</span>
        <span className="hidden sm:inline ml-1 text-[11px]">{text}</span>
      </>
    )
  );

  return (
    <div className={wrapperClass} data-testid={`invoice-actions-${invoice.id}`}>
      <Button variant="ghost" size="sm" onClick={() => onView(invoice)} title="Vizualizare" className={btnBase}>
        <Eye className="w-4 h-4" /><Label text="Vezi" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onEdit(invoice)} className={`text-blue-500 ${btnBase}`} title="Editează">
        <Edit className="w-4 h-4" /><Label text="Edit" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onDownloadPDF(invoice)} className={`text-blue-600 ${btnBase}`} title="Descarcă PDF">
        <Download className="w-4 h-4" /><Label text="PDF" />
      </Button>
      {onDownloadXML && (
        <Button variant="ghost" size="sm" onClick={() => onDownloadXML(invoice)} className={`text-teal-600 ${btnBase}`} title="Descarcă XML e-Factura" data-testid={`xml-download-${invoice.id}`}>
          <Code className="w-4 h-4" /><Label text="XML" />
        </Button>
      )}
      {invoice.status === "draft" && (
        <Button variant="ghost" size="sm" onClick={() => onEmit(invoice.id)} className={`text-green-600 ${btnBase}`} title="Emite">
          <Send className="w-4 h-4" /><Label text="Emite" />
        </Button>
      )}
      {invoice.status === "emisa" && !invoice.anaf_status && onSendToAnaf && anafConfigured && (
        <Button variant="ghost" size="sm" onClick={() => onSendToAnaf(invoice.id)} className={`text-teal-600 ${btnBase}`} title="Trimite la ANAF" disabled={sendingToAnaf === invoice.id} data-testid={`anaf-send-${invoice.id}`}>
          {sendingToAnaf === invoice.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
          <Label text="ANAF" />
        </Button>
      )}
      {invoice.anaf_status && invoice.anaf_status !== "acceptata" && onCheckAnafStatus && (
        <Button variant="ghost" size="sm" onClick={() => onCheckAnafStatus(invoice.id)} className={`text-amber-600 ${btnBase}`} title="Verifică status ANAF" disabled={checkingAnafStatus === invoice.id}>
          {checkingAnafStatus === invoice.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <Label text="Status" />
        </Button>
      )}
      {invoice.status === "emisa" && (
        <Button variant="ghost" size="sm" onClick={() => onPaid(invoice.id)} className={`text-green-600 ${btnBase}`} title="Marchează plătită">
          <CheckCircle className="w-4 h-4" /><Label text="Plătită" />
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={() => onDelete(invoice.id)} className={`text-red-500 ${btnBase}`} title="Șterge">
        <Trash2 className="w-4 h-4" /><Label text="Șterge" />
      </Button>
    </div>
  );
}

export function InvoiceList({
  invoices, loading, onEmit, onPaid, onDelete, onView, onEdit,
  onDownloadPDF, onDownloadXML, onPreviewXML, onSendToAnaf, onCheckAnafStatus,
  sendingToAnaf, checkingAnafStatus, anafConfigured, StatusBadge, AnafStatusBadge, isPrimite,
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-50 rounded-lg">
        <Receipt className="w-12 h-12 mx-auto text-slate-400 mb-4" />
        <h3 className="text-lg font-medium text-slate-600 mb-2">
          Nu există facturi {isPrimite ? "primite" : "emise"}
        </h3>
        <p className="text-slate-500">Creează prima factură pentru a începe</p>
      </div>
    );
  }

  const actionProps = {
    onView, onEdit, onDownloadPDF, onDownloadXML, onEmit,
    onSendToAnaf, onCheckAnafStatus, onPaid, onDelete,
    sendingToAnaf, checkingAnafStatus, anafConfigured,
  };

  return (
    <>
      {/* MOBILE — cards verticale (text deasupra, butoane dedesubt — ca la Devize) */}
      <div className="sm:hidden space-y-3" data-testid="invoice-list-mobile">
        {invoices.map((invoice) => (
          <div
            key={invoice.id}
            className="bg-slate-50 rounded-lg p-3 border border-slate-200"
            data-testid={`invoice-card-${invoice.id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-bold text-slate-800 text-sm">
                    {invoice.series}/{invoice.number}
                  </span>
                  <StatusBadge status={invoice.status} />
                  {AnafStatusBadge && <AnafStatusBadge invoice={invoice} />}
                </div>
                <p className="text-sm text-slate-700 font-medium truncate" title={invoice.client_name}>
                  {invoice.client_name}
                </p>
                {invoice.client_cui && (
                  <p className="text-[11px] text-slate-500">CUI: {invoice.client_cui}</p>
                )}
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {formatDateRO(invoice.invoice_date)}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-base font-bold text-slate-800">
                  {formatNumber(invoice.total)}
                </div>
                <div className="text-[10px] text-slate-500">RON</div>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-200">
              <InvoiceActions invoice={invoice} {...actionProps} isMobile />
            </div>
          </div>
        ))}
      </div>

      {/* DESKTOP — tabel clasic */}
      <div className="hidden sm:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Serie/Nr.</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Client</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Acțiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell className="font-medium">{invoice.series}/{invoice.number}</TableCell>
                <TableCell>{formatDateRO(invoice.invoice_date)}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{invoice.client_name}</p>
                    {invoice.client_cui && <p className="text-xs text-slate-500">CUI: {invoice.client_cui}</p>}
                  </div>
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatNumber(invoice.total)} RON
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <StatusBadge status={invoice.status} />
                    {AnafStatusBadge && <AnafStatusBadge invoice={invoice} />}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <InvoiceActions invoice={invoice} {...actionProps} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
